import { capitalize } from 'lodash';
import React from 'react';
import {
  businessProcessFlows,
  businessProcessSwimlanes,
  featuredShapes,
  groupedShapes,
} from '../businessProcess/definitions';
import {
  makeBusinessProcessFlowItem,
  makeBusinessProcessItem,
} from '../businessProcess/menuItems';
import { type CopiedWhiteboardContent } from '../collaboration/Serialization';
import type WhiteboardController from '../controllers/WhiteboardController';
import {
  type CustomShape,
  type CustomShapeClipboardData,
} from '../models/CustomShape';
import { isMenuItemDefinition } from '../stylus/definitions';
import StylusSwimlane from '../stylus/StylusSwimlane';
import { type IconTabItem } from '../toolbars/ObjectMenu/IconsTab';
import { type ObjectMenuSection } from '../toolbars/ObjectMenu/ObjectMenu';
import { type ShapeTabItem } from '../toolbars/ObjectMenu/ShapesTab';
import { SpriteSvg } from '../toolbars/ObjectMenu/SpriteSvg';
import { type SpriteSvgItem } from '../types';
import { iconComponent, iconList, isFaIcon } from './icons';
import { capitalCase, sentenceCase } from './string';
import { makeTestId } from './tools';
import version from '../version';

type TabItem = ShapeTabItem | IconTabItem;

const copyAllCustomShapesToWhiteboard = (controller: WhiteboardController) => {
  const shapeToClipboardData = (
    customShape: CustomShape
  ): CustomShapeClipboardData => ({
    id: customShape.id,
    name: customShape.name,
    objects: customShape.objects,
  });

  const clipboardContent: CopiedWhiteboardContent = {
    whiteboardId: controller.whiteboardId,
    objects: {},
    customShapes: Object.fromEntries(
      controller.customShapesStyluses.map(({ customShape }) => [
        customShape.id,
        shapeToClipboardData(customShape),
      ])
    ),
    version,
  };

  void navigator.clipboard
    .writeText(JSON.stringify(clipboardContent))
    .then(() => {
      controller.actionShowMessage(
        'Paste into a different whiteboard to add custom shapes to it'
      );
    });
};

const makeCustomShapesSection = (
  controller: WhiteboardController
): ObjectMenuSection<ShapeTabItem> => {
  const title = 'Custom shapes';
  const hasWhiteboardsAdvanced = window.currentAccount?.whiteboardsAdvanced;

  if (!hasWhiteboardsAdvanced) {
    return {
      title,
      promoLink:
        '/suite/whiteboards?upgrade_location=whiteboards_custom_shapes_menu#streamline_diagram_creation',
      items: [],
      sections: [],
      tooltip: false,
    };
  }

  const shapes = controller.customShapesStyluses;
  // How many custom shapes to display in the main Shapes menu.
  const maxShapesBeforeSubSection = 12;

  const titleElement = () => {
    return (
      <div
        style={
          hasWhiteboardsAdvanced
            ? {
                display: 'flex',
                width: '100%',
                justifyContent: 'space-between',
                alignItems: 'center',
              }
            : {}
        }
      >
        <>
          <div>
            <span>Custom shapes </span>
            <aha-tooltip-default-trigger></aha-tooltip-default-trigger>
            <aha-tooltip placement='top'>
              <span>
                Select shapes on your whiteboard and choose &quot;Save as custom
                shape&quot; from the … menu to add a custom shape here.
              </span>
            </aha-tooltip>
          </div>
          <aha-button
            kind='icon'
            title='Copies all custom shapes to the clipboard. Paste into a different whiteboard to add them as new custom shapes'
            disabled={shapes.length === 0 ? true : undefined}
            onClick={e => {
              // Stop propagation and reset the stylus to prevent the default event handler
              // that would mean that the next click on the whiteboard would insert a shape.
              e.stopPropagation();
              controller.actionSetStylus(controller.stylusPointer);
              copyAllCustomShapesToWhiteboard(controller);
            }}
          >
            <aha-icon icon='fa-regular fa-copy' />
          </aha-button>
        </>
      </div>
    );
  };

  return {
    title,
    titleElement,
    get items() {
      return shapes.length < maxShapesBeforeSubSection
        ? shapes
        : shapes.slice(0, maxShapesBeforeSubSection);
    },
    get sections() {
      return shapes.length > maxShapesBeforeSubSection
        ? [
            {
              title,
              titleElement,
              items: shapes,
              sections: [],
              tooltip: false,
            },
          ]
        : [];
    },
    tooltip: false,
  };
};

export const generateShapesMenuStructure = (
  controller: WhiteboardController
): ObjectMenuSection<ShapeTabItem>[] => {
  const userActions = [
    'button',
    'multiSelect',
    'link',
    'checkbox',
    'radioButton',
    'toggle',
    'slider',
    'pill',
  ];

  const navigation = [
    'navMenuVertical',
    'navMenuHorizontal',
    'dropdown',
    'scrollBar',
    'pagination',
    'breadcrumb',
  ];

  const inputElements = ['inputField', 'dropdown', 'datePicker', 'searchBar'];

  const placeholders = [
    'placeholder',
    'image',
    'donutChart',
    'video',
    'map',
    'pieChart',
    'lineChart',
    'barChart',
    'avatar',
  ];

  const {
    shapeStyluses,
    advancedStyluses,
    wireframeStyluses,
    blockStyluses,
    deviceStyluses,
    allWireframes,
    smartRecordStyluses,
  } = controller;

  const wireframesByGroup = [
    {
      title: 'User actions',
      items: wireframeStyluses.filter(stylus =>
        userActions.includes(stylus.wireframeName)
      ),
      sections: [],
    },
    {
      title: 'Navigation',
      items: wireframeStyluses.filter(stylus =>
        navigation.includes(stylus.wireframeName)
      ),
      sections: [],
    },
    {
      title: 'Input elements',
      items: wireframeStyluses.filter(stylus =>
        inputElements.includes(stylus.wireframeName)
      ),
      sections: [],
    },
    {
      title: 'Placeholders',
      items: wireframeStyluses.filter(stylus =>
        placeholders.includes(stylus.wireframeName)
      ),
      sections: [],
    },
    {
      title: 'Devices',
      items: deviceStyluses,
      sections: [],
    },
    {
      title: 'Blocks',
      items: blockStyluses,
      sections: [],
    },
  ];

  const sections: ObjectMenuSection<ShapeTabItem>[] = [
    {
      title: 'Basic shapes',
      items: shapeStyluses,
      sections: [],
      tooltip: true,
    },
    {
      title: 'Aha! shapes',
      items: smartRecordStyluses,
      sections: [],
    },
    {
      title: 'Advanced shapes',
      items: advancedStyluses,
      sections: [],
      tooltip: false,
    },
  ];

  if (
    window.currentAccount?.whiteboardsAdvanced ||
    window.currentAccount?.hasCrossProductPromotionsEnabled
  ) {
    sections.push({
      title: 'Wireframes',
      items: allWireframes
        .slice(0, 12)
        .map(wireframeName =>
          wireframeStyluses.find(
            stylus => stylus.wireframeName === wireframeName
          )
        )
        .filter(Boolean),
      sections: wireframesByGroup,
      tooltip: false,
      promoLink: () =>
        !window.currentAccount?.whiteboardsAdvanced
          ? '/suite/whiteboards?upgrade_location=whiteboards_shape_menu_wireframe'
          : undefined,
    });

    sections.push({
      title: 'Business processes',
      tooltip: true,
      items: featuredShapes.map(makeBusinessProcessItem),
      sections: [
        ...groupedShapes.map(group => ({
          title: group.title,
          tooltip: true,
          items: group.shapes.map(makeBusinessProcessItem),
          sections: [],
        })),
        {
          title: 'Flows',
          tooltip: true,
          items: businessProcessFlows.map(
            makeBusinessProcessFlowItem(controller)
          ),
          sections: [],
        },
        {
          title: 'Swimlanes',
          tooltip: true,
          items: businessProcessSwimlanes.map(
            ({ id }) => new StylusSwimlane({ controller, id })
          ),
          sections: [],
        },
      ],
      promoLink: () =>
        !window.currentAccount?.whiteboardsAdvanced
          ? '/suite/whiteboards?upgrade_location=whiteboards_shape_menu_business_process'
          : undefined,
    });
  }

  // If they don't have whiteboards advanced and we can't promote it
  // then don't show any part of the custom shapes section.
  if (
    window.currentAccount?.whiteboardsAdvanced ||
    window.currentAccount?.hasCrossProductPromotionsEnabled
  ) {
    sections.push(makeCustomShapesSection(controller));
  }

  return sections;
};

export const generateIconsMenuStructure = (
  spriteIconSections: ObjectMenuSection<SpriteSvgItem>[]
) => {
  const fontAwesomeIcons = iconList();
  if (!window.currentAccount?.whiteboardsAdvanced) {
    spriteIconSections.forEach(section => {
      section.promoLink = `/suite/whiteboards?upgrade_location=whiteboards_shape_menu_${section.title.toLowerCase()}`;
    });
  }

  return {
    name: 'Icons',
    sections: [
      {
        title: 'Basic icons',
        isFaSection: true,
        items: fontAwesomeIcons,
        sections: [
          {
            // Ideally we could restruct this in the component, but we get
            // proxy arrays at that point which cant be mapped.
            title: 'Basic icons',
            isFaSection: true,
            items: fontAwesomeIcons,
            sections: [],
          },
        ],
      },
      ...spriteIconSections,
    ],
  };
};

export const RenderSpriteAsIcon = ({ item }) => {
  return (
    <svg width='26' height='26' viewBox={item.viewBox || '0 0 26 26'}>
      <use href={SpriteSvg.getInstance().svgRef(item.path)} />
    </svg>
  );
};

export const testId = (name: string) => `Button-${makeTestId(name)}`;

export const iconTitleize = (title: string, isSection = false) => {
  const aiRegex = /\bai\b/i;
  const iotRegex = /\biot\b/i;
  const qnaRegex = /\bqna\b/i;
  const osRegex = /\bos\b/i;
  const apiRegex = /\bapi\b/i;
  const apisRegex = /\bapis\b/i;
  const ciRegex = /\bci\b/i;
  const cdRegex = /\bcd\b/i;
  const ciCdRegex = /\bci[\s\-_]*cd\b/i;
  const webMobileRegex = /\bweb mobile\b/i;

  title = title.replace(/_/g, ' ');

  // Other than replacing underscores, we don't want to change titles
  if (['google cloud', 'azure', 'aws'].includes(title.toLowerCase())) {
    return capitalCase(title);
  }

  if (title.toLowerCase().includes('basic icons') && !isSection) {
    return 'basic icons';
  }

  if (title.includes('+')) {
    title = title.replace('+', '&');
  }

  if (isSection) {
    title = sentenceCase(title);
  }

  // Handle acronyms
  title = title.replace(aiRegex, 'AI');
  title = title.replace(iotRegex, 'Internet of Things');
  title = title.replace(qnaRegex, 'QnA');
  title = title.replace(osRegex, 'OS');
  title = title.replace(apiRegex, 'API');
  title = title.replace(apisRegex, 'APIs');
  title = title.replace(ciRegex, 'CI');
  title = title.replace(cdRegex, 'CD');
  title = title.replace(ciCdRegex, 'CI/CD');
  title = title.replace(webMobileRegex, 'web/mobile');

  return title;
};

export const isShapeTabItem = (item: TabItem): item is ShapeTabItem =>
  isMenuItemDefinition(item) || item.constructor?.name.includes('Stylus');

export const isIconTabItem = (item: TabItem): item is IconTabItem => {
  return 'path' in item || 'iconName' in item;
};

export const flattenSections = <T,>(sections: ObjectMenuSection<T>[]): T[] => {
  let items: T[] = [];

  function extractItems(section: ObjectMenuSection<T>) {
    if (section.items) {
      items = items.concat(section.items);
    }

    section.sections.forEach(extractItems);
  }

  sections.forEach(extractItems);

  return items;
};

export const iconTabItemStylusName = (item: IconTabItem) => {
  return isFaIcon(item) ? `${item.prefix}/${item.iconName}` : item.path;
};

export const iconDisplayName = (item: IconTabItem) => {
  return isFaIcon(item)
    ? item.iconName.replace(/-/g, ' ')
    : SpriteSvg.getInstance().displayName(item.path);
};

export const searchResultItemComponent = (item: TabItem) => {
  if (isShapeTabItem(item)) {
    return item.renderIcon();
  }

  return iconComponent(item);
};

export const searchResultItemDescription = (item: TabItem) => {
  if (isShapeTabItem(item)) {
    return item.description();
  }

  return iconDisplayName(item);
};

const upgradeLocation = (promoLink: string) => {
  if (promoLink) {
    const upgradeLocationParam = promoLink.split('?upgrade_location=').pop();
    return capitalize(upgradeLocationParam)?.replace(/_/g, ' ');
  }
  return '';
};

export const handlePromoClick = (promoLink: string) => {
  const location = upgradeLocation(promoLink);
  if (promoLink) {
    window.open(promoLink, '_blank');

    window.AhaAnalytics?.track(
      'Feature-7333230858416702915',
      'User clicks promotion',
      {
        'Upgrade location': location,
        'Promotion product': 'Whiteboards',
        'Originating product': window.currentAccount?.primaryFlavor,
      }
    );
  }
};
